import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowLeft, MapPin, Globe, Facebook, Twitter, Youtube, Instagram } from 'lucide-react';
import ProfileAvatar from '../components/ProfileAvatar';

interface PersonProfileData {
  id: string;
  name: string;
  image: string;
  bio: string;
  iwi: string;
  hapu: string;
  location: string;
  role: string;
  achievements: string[];
  interests: string[];
  socialMedia?: {
    website?: string;
    facebook?: string;
    twitter?: string;
    youtube?: string;
    instagram?: string;
  };
}

const sampleProfiles: Record<string, PersonProfileData> = {
  'billy-turner': {
    id: 'billy-turner',
    name: 'Billy Turner',
    image: '/api/placeholder/400/400',
    bio: 'Billy is a successful entrepreneur and millionaire business leader who advocates for Māori economic sovereignty through business excellence. He promotes the concept that Aotearoa operates under Te Moana Nui a Kiwa jurisdiction and provides step-by-step guidance for exercising sovereignty rights.',
    iwi: 'Ngāti Tūwharetoa',
    hapu: 'Ngāti Tūrangitukua',
    location: 'Taupō, Central North Island',
    role: 'Business Leader & Sovereignty Advocate',
    achievements: [
      'Built multi-million dollar business empire while maintaining cultural values',
      'Developed educational materials on jurisdictional sovereignty',
      'Mentored Māori entrepreneurs in economic independence',
      'Guest speaker for Māori Ranger Security Division'
    ],
    interests: ['Business development', 'Economic sovereignty', 'Jurisdictional law', 'Wealth creation']
  },
  'peter-martin': {
    id: 'peter-martin',
    name: 'Peter Martin',
    image: '/api/placeholder/400/400',
    bio: 'Peter leads the Common Law Embassy NZ and is known for creating Māori ranger diplomatic immunity cards. He educates communities on common law principles and challenges conventional legal frameworks through alternative documentation systems.',
    iwi: 'Ngāti Manu',
    hapu: 'Te Uri-o-Hau',
    location: 'Auckland, Tāmaki Makaurau',
    role: 'Common Law Educator & Legal Rights Advocate',
    achievements: [
      'Established Common Law Embassy NZ organization',
      'Created and distributed Māori ranger diplomatic immunity cards',
      'Educated hundreds on common law principles',
      'Challenged conventional legal frameworks through documentation'
    ],
    interests: ['Common law education', 'Diplomatic immunity', 'Legal sovereignty', 'Alternative governance']
  },
  'new-zealand-rangers': {
    id: 'new-zealand-rangers',
    name: 'Māori Ranger Security Division',
    image: '/api/placeholder/400/400',
    bio: 'The Māori Ranger Security Division protects and assists people who become victims of unlawful corporate structures. They claim jurisdiction higher than New Zealand Police and provide security services for hapū asserting mana motuhake on their ancestral lands.',
    iwi: 'Multi-iwi collective',
    hapu: 'Supporting all hapū',
    location: 'Nationwide, Aotearoa',
    role: 'Hapū Protection & Security Collective',
    achievements: [
      'Established hapū security services across multiple regions',
      'Protected communities from corporate land grabbing',
      'Provided manaki/tautoko to whānau exploring tikanga practices',
      'Maintained mana i te whenua against multiple corporate entities'
    ],
    interests: ['Hapū security', 'Territorial protection', 'Customary law enforcement', 'Community safety']
  },
  'kiri-campbell': {
    id: 'kiri-campbell',
    name: 'Kiri Campbell',
    image: '/api/placeholder/400/400',
    bio: 'Kiri Campbell is a courageous advocate from Taranaki who brought national attention to banking system practices and monetary reform. Her bold actions sparked important community discussions about financial institutions and inspired many people to question conventional banking systems. She demonstrated remarkable bravery in challenging established systems for the benefit of her community.',
    iwi: 'Taranaki',
    hapu: 'Ngāti Ruanui',
    location: 'Hawera, Taranaki',
    role: 'Banking System Reformer & Community Advocate',
    achievements: [
      'Brought national attention to banking system practices',
      'Sparked nationwide discussion about monetary reform',
      'Attracted strong community support with 1600+ supporters',
      'Became an inspiration for community activism and reform',
      'Demonstrated courage in challenging institutional practices',
      'Educated the public about alternative financial systems'
    ],
    interests: ['Banking system reform', 'Community advocacy', 'Monetary system education', 'Financial literacy'],
    socialMedia: {
      twitter: 'https://twitter.com/kcamp72'
    }
  },
  'joseph-murphy': {
    id: 'joseph-murphy',
    name: 'Joseph Murphy',
    image: '/api/placeholder/400/400',
    bio: 'Joseph served as "chief justice" of the Northland-based Nga Tikanga Law Society, which issued Māori driver licenses and number plates. The organization was linked to sovereignty claims and occupations, promoting parallel Māori legal systems.',
    iwi: 'Ngāpuhi',
    hapu: 'Ngāti Hine',
    location: 'Kaikohe, Northland',
    role: 'Former Chief Justice, Nga Tikanga Law Society',
    achievements: [
      'Led Nga Tikanga Law Society operations',
      'Issued Māori driver licenses and number plates',
      'Coordinated sovereignty claims across North Island',
      'Established parallel Māori legal framework'
    ],
    interests: ['Māori legal systems', 'Parallel governance', 'Sovereignty claims', 'Traditional law']
  },
  'johnny-tupo': {
    id: 'johnny-tupo',
    name: 'Johnny "Two Guns" Tupo',
    image: '/api/placeholder/400/400',
    bio: 'Johnny "Two Guns" Tupo is known for his direct action approach to sovereignty advocacy, earning his nickname through fearless confrontation of authority and unwavering commitment to Māori rights. His activism focuses on immediate intervention and challenging oppressive systems through bold action.',
    iwi: 'Ngāti Tuwharetoa',
    hapu: 'Ngāti Hinemihi',
    location: 'Rotorua, Bay of Plenty',
    role: 'Direct Action Activist',
    achievements: [
      'Led numerous direct action protests for Māori rights',
      'Confronted government officials on sovereignty issues',
      'Organized community resistance against land seizures',
      'Mentored younger activists in direct action tactics'
    ],
    interests: ['Direct action', 'Confrontational activism', 'Community organizing', 'Youth mentorship']
  },
  'forestry-hilton': {
    id: 'forestry-hilton',
    name: 'Forestry Hilton',
    image: '/api/placeholder/400/400',
    bio: 'Forestry Hilton is developing alternative banking systems that challenge traditional financial structures and promote Māori economic independence. His work focuses on creating financial sovereignty through innovative banking solutions that operate outside conventional monetary systems.',
    iwi: 'Ngāti Awa',
    hapu: 'Ngāti Pūkeko',
    location: 'Whakatāne, Bay of Plenty',
    role: 'Alternative Banking Systems Developer',
    achievements: [
      'Developed prototype alternative banking system',
      'Challenged traditional monetary structures',
      'Educated communities on financial sovereignty',
      'Created frameworks for economic independence'
    ],
    interests: ['Alternative banking', 'Financial systems', 'Economic sovereignty', 'Monetary reform']
  },
  'matt-tarawa': {
    id: 'matt-tarawa',
    name: 'Matt Tarawa',
    image: '/api/placeholder/400/400',
    bio: 'Matt Tarawa is a strategic thinker focused on practical pathways to sovereignty, developing comprehensive frameworks for hapū self-determination. His approach combines traditional knowledge with modern strategic planning to create actionable roadmaps for tino rangatiratanga.',
    iwi: 'Ngāti Kahungunu',
    hapu: 'Ngāti Kere',
    location: 'Hastings, Hawke\'s Bay',
    role: 'Sovereignty Strategist',
    achievements: [
      'Developed strategic frameworks for hapū self-determination',
      'Created practical sovereignty implementation plans',
      'Facilitated strategic planning workshops across multiple iwi',
      'Authored papers on sovereignty pathway strategies'
    ],
    interests: ['Sovereignty strategy', 'Self-determination', 'Strategic planning', 'Policy development']
  },
  'peter-singh': {
    id: 'peter-singh',
    name: 'Peter Singh',
    image: '/api/placeholder/400/400',
    bio: 'Peter Singh leads the Ko Hui o Rau organization that provides crucial support and resources for tino rangatiratanga movements across Aotearoa. His multicultural background brings unique perspectives to supporting Māori sovereignty while building bridges between diverse communities.',
    iwi: 'Multi-cultural',
    hapu: 'Supporting tino rangatiratanga',
    location: 'Wellington, Te Whanganui-a-Tara',
    role: 'Ko Hui o Rau Coordinator',
    achievements: [
      'Established Ko Hui o Rau support network',
      'Coordinated resources for tino rangatiratanga movements',
      'Built multicultural alliances for Māori sovereignty',
      'Facilitated cross-community collaboration'
    ],
    interests: ['Tino rangatiratanga support', 'Community coordination', 'Multicultural alliances', 'Resource management']
  }
};

const PersonProfile = () => {
  const { personId } = useParams<{ personId: string }>();
  const profile = personId ? sampleProfiles[personId] : null;

  useEffect(() => {
    if (profile) {
      document.title = `${profile.name} | He Whakaputanga nga Hapū o te Tairawhiti`;
    } else {
      document.title = 'Profile Not Found | He Whakaputanga nga Hapū o te Tairawhiti';
    }
    return () => {
      document.title = 'He Whakaputanga nga Hapū o te Tairawhiti | Customary Rights Platform';
    };
  }, [profile]);

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Profile Not Found</h1>
          <Link to="/" className="btn-primary">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <Link to="/" className="inline-flex items-center text-ocean-teal hover:text-ocean-teal-dark mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Profile Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Profile Header */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <ProfileAvatar 
                  name={profile.name} 
                  size="xl" 
                  className="mx-auto lg:mx-0"
                />
              </div>
              <div className="flex-grow text-center lg:text-left">
                <h1 className="text-4xl font-bold text-gray-800 mb-2">{profile.name}</h1>
                <p className="text-xl text-ocean-teal mb-4">{profile.role}</p>
                <div className="flex flex-wrap gap-4 justify-center lg:justify-start text-gray-600 mb-6">
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2" />
                    {profile.location}
                  </div>
                </div>
                <div className="cultural-border bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-2">
                    <strong>Iwi:</strong> {profile.iwi} | <strong>Hapū:</strong> {profile.hapu}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bio Section */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">About</h2>
            <p className="text-gray-700 leading-relaxed text-lg">{profile.bio}</p>
          </div>

          {/* Achievements & Interests Grid */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Achievements */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Key Achievements</h2>
              <ul className="space-y-4">
                {profile.achievements.map((achievement, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-ocean-teal rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">{achievement}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Interests */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Areas of Interest</h2>
              <div className="flex flex-wrap gap-3">
                {profile.interests.map((interest, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-ocean-teal/10 text-ocean-teal rounded-full text-sm font-medium"
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Social Media Links */}
          {profile.socialMedia && Object.keys(profile.socialMedia).length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-8 mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Connect Online</h2>
              <div className="flex flex-wrap gap-4">
                {profile.socialMedia.website && (
                  <a
                    href={profile.socialMedia.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    Website
                  </a>
                )}
                {profile.socialMedia.facebook && (
                  <a
                    href={profile.socialMedia.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                  >
                    <Facebook className="h-4 w-4 mr-2" />
                    Facebook
                  </a>
                )}
                {profile.socialMedia.twitter && (
                  <a
                    href={profile.socialMedia.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-sky-100 hover:bg-sky-200 text-sky-700 rounded-lg transition-colors"
                  >
                    <Twitter className="h-4 w-4 mr-2" />
                    Twitter
                  </a>
                )}
                {profile.socialMedia.youtube && (
                  <a
                    href={profile.socialMedia.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                  >
                    <Youtube className="h-4 w-4 mr-2" />
                    YouTube
                  </a>
                )}
                {profile.socialMedia.instagram && (
                  <a
                    href={profile.socialMedia.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-pink-100 hover:bg-pink-200 text-pink-700 rounded-lg transition-colors"
                  >
                    <Instagram className="h-4 w-4 mr-2" />
                    Instagram
                  </a>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PersonProfile;